'use client';

import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {useForm, type UseFormSetValue} from 'react-hook-form';
import {zodResolver} from '@hookform/resolvers/zod';
import {Plus, X} from 'lucide-react';
import {Input} from '@/matic/components/ui/input';
import {Toggle} from '@/matic/components/ui/toggle';
import {useApiToken} from '@/matic/hooks/useApiClient';
import {useAppContext} from '@/matic/providers/AppProvider';
import {useNextSuffix} from '@/matic/features/master-data/hooks/useNextSuffix';
import {MasterDataFieldRenderer} from './fields/MasterDataFieldRenderer';
import {AddFieldForm} from './AddFieldForm';
import {
  TRIBAL_KNOWLEDGE_FIELD_KEY,
  LONG_TEXT_FIELD_KEYS,
} from '@/matic/features/master-data/constants';
import type {
  ObjectType,
  ObjectTypeField,
} from '@/matic/features/master-data/types/master-data-types';
import {buildDynamicSchema} from '@/matic/features/_shared/utils/build-dynamic-schema';
import {
  coerceFieldWriteForStaging,
  type CoercionFieldSchema,
} from '@/matic/features/_shared/utils/field-write-coercion';
import {createObjectReferenceCodeResolver} from '@/matic/features/_shared/object-reference/resolve-object-reference-code';
import {SyncedFieldMarker} from '@/matic/features/erp/record-provenance/components/SyncedFieldMarker';
import {
  QUOTE_DESK_FIELD_KEYS,
  QUOTE_MODULE_TAG,
  QUOTE_PROGRESS_FIELD_KEYS,
} from '@/matic/features/quoting/quote-desk-fields';
import {
  CetecFieldWriteDialog,
  canWriteErpFields,
  WriteBackFieldMarker,
} from '@/matic/features/erp/record-field-write';

/**
 * Maps an ObjectType field to the coercion schema shape expected by
 * `coerceFieldWriteForStaging`. MDM's `object_reference` fields are always
 * single-value (see `MasterDataFieldRenderer`); `table` doesn't exist as an
 * MDM field type. Everything else passes through unchanged.
 */
function toCoercionSchema(field: ObjectTypeField): CoercionFieldSchema {
  switch (field.fieldType) {
    case 'multiselect': {
      return {kind: 'multi_select', options: field.config?.options};
    }

    case 'boolean': {
      return {kind: 'checkbox'};
    }

    case 'number': {
      return {kind: 'number'};
    }

    case 'object_reference': {
      return {
        kind: 'object_reference',
        referenceObjectTypeId: field.config?.referenceObjectTypeId,
        multi: false,
      };
    }

    case 'text':
    case 'dropdown':
    case 'date': {
      return {kind: 'other'};
    }
  }
}

export type ObjectRecordSubmit = {
  data: Record<string, unknown>;
  codeSuffix?: string;
};

/**
 * Imperative handle (opt-in via `ref`) for hosts that drive the form
 * programmatically — the Otto executor's editable RHS stages streamed
 * `field_write`s via `applyFieldWrite`, then Saves the current field data via
 * `getPendingChanges`. The admin create/edit modals ignore the ref.
 */
export type ObjectRecordFormHandle = {
  /**
   * Apply a streamed field write into the form. The value is coerced into
   * persist-shape (see `toCoercionSchema` / `field-write-coercion.ts`) but
   * NOT validated against the zod schema — `buildDynamicSchema` only checks
   * required-ness (`z.unknown()` for everything else), so a malformed value
   * that survives coercion won't be caught until the record-write PATCH.
   */
  applyFieldWrite: (fieldKey: string, value: unknown) => void;
  /**
   * Current field data as the edit-mode submit shape (a value for every active
   * field, `null` when empty) — the payload for `updateObjectRecord`.
   */
  getPendingChanges: () => Record<string, unknown>;
  /**
   * Persist the currently-staged values through the same validated path the
   * Save button uses (RHF `handleSubmit` → the parent's `onValidSubmit` →
   * `updateMutation.mutateAsync`). Lets an Otto host auto-commit staged
   * field-writes at turn-settle / record-swap without a manual Save.
   *
   * Idempotent: resolves `true` without a PATCH when nothing changed vs the
   * last-persisted seed. Resolves `false` (staged values kept) when RHF
   * validation blocks or the PATCH rejects — never throws into the stream.
   */
  submitPending: () => Promise<boolean>;
};

type ObjectRecordFormProps = {
  objectType: ObjectType;
  /**
   * Id of the `<form>` element. A submit button rendered outside this form (the
   * host's footer/header) wires to it via `type="submit" form={formId}`, so the
   * host owns the chrome while this component owns the fields + validation.
   */
  formId: string;
  onValidSubmit: (payload: ObjectRecordSubmit) => void | Promise<void>;
  serverErrors?: Record<string, string>;
  /**
   * `create` (default) seeds an empty form and exposes the system-code escape
   * hatch. `edit` seeds from `initialData`, drops the code controls, and
   * submits a value for *every* active field (or `null` when cleared) so the
   * server-side merge reproduces the exact form state.
   */
  mode?: 'create' | 'edit';
  /** Edit-mode seed values, keyed by field key (the record's current data). */
  initialData?: Record<string, unknown>;
  /**
   * When true, only the lead fields (required + display + primary-info text)
   * render up front; the remaining supplementary fields appear as compact
   * click-to-add rows. Default false → every field renders expanded (admin
   * master-data behavior).
   */
  collapseOptionalFields?: boolean;
  /**
   * When true, render the inline "+ Add field" affordance below the fields so a
   * new attribute can be added to the type on the fly.
   */
  allowAddField?: boolean;
  /** Render fields as non-editable while preserving preview/read layout. */
  disabled?: boolean;
  /**
   * Field keys fed by an ERP sync, rendered read-only with a "from Cetec"
   * marker.
   *
   * Per-field, never per-record: the record is the tenant's, and only the
   * fields the admin explicitly mapped are a Cetec-fed projection inside
   * it. Everything else — tribal knowledge above all — stays editable.
   *
   * Read-only is the honest option rather than a paternalistic one: an
   * edit here is *guaranteed* to be overwritten on the next sync run, so
   * blocking it beats letting it silently revert later. The escape hatch
   * is unmapping the field, which is already supported.
   */
  erpManagedFieldKeys?: readonly string[];
  /**
   * The subset of `erpManagedFieldKeys` that can be written *back* to the
   * ERP. Same lock, different resolution: instead of "an edit here
   * wouldn't stick", these get an "Edit in Cetec" affordance that writes
   * the change to the ERP first and refreshes MATIC's copy after.
   *
   * The input itself stays disabled either way. Edit mode submits every
   * active field on save, so an enabled input would fire an ERP write for
   * a field the user never touched — and the confirm-diff round trip
   * would have to interleave with react-hook-form's own validation
   * lifecycle. The per-field dialog keeps the write path explicit.
   */
  erpWritableFieldKeys?: readonly string[];
  /**
   * The MDM record being edited. Required for write-through — the ERP
   * write targets a specific claimed record — so the affordance stays
   * hidden on create, where there is no record to write back for.
   */
  objectRecordId?: string;
  /**
   * Opt-in subscription to the form's live field values. Emits once on mount
   * and again on every field change, so a host (Otto's RHS inspector) can
   * render a live current→proposed diff strip. The admin create/edit modals
   * omit it, so they're unaffected.
   */
  onValuesChange?: (values: Record<string, unknown>) => void;
  /**
   * Opt-in dirty-state subscription for hosts that need guarded close / disabled
   * Save behavior. Uses React Hook Form's persisted-seed comparison.
   */
  onDirtyChange?: (isDirty: boolean) => void;
};

/**
 * Free-text fields that describe a record rather than classify it. Otto's
 * default ObjectType shape is `name` + `tribal_knowledge` (the freeform
 * context catch-all), and it routinely proposes adding a `notes` field —
 * these read as part of "the record's info", so they lead alongside
 * required + display fields rather than hiding as supplementary.
 */
const PRIMARY_INFO_FIELD_KEYS = new Set([
  TRIBAL_KNOWLEDGE_FIELD_KEY,
  'description',
  'notes',
  'summary',
  'details',
  'info',
]);

function isPrimaryInfoField(field: ObjectTypeField): boolean {
  return (
    field.fieldType === 'text' && PRIMARY_INFO_FIELD_KEYS.has(field.fieldKey)
  );
}

/** A value that should submit as `null` (empty input, cleared, or removed). */
function isEmptyValue(value: unknown): boolean {
  return (
    value === undefined ||
    value === null ||
    value === '' ||
    (Array.isArray(value) && value.length === 0)
  );
}

/**
 * Edit-mode submit data: a value for every active field — the entered value, or
 * `null` when empty/removed — so the server-side merge reproduces the exact form
 * state and cleared optionals actually persist as cleared.
 *
 * **ERP-managed fields are excluded**, and that exclusion is a correctness
 * fix rather than a tidy-up. Those fields are owned by the ERP: the only
 * legitimate way to change one is the write-through path, which writes the
 * ERP *first* and lands the value locally only after reading it back
 * confirmed. Sending one through the ordinary MDM PATCH writes it locally
 * and nowhere else, so the next sync silently reverts it — which is exactly
 * the divergence the read-only lock exists to prevent.
 *
 * The lock on the input is not sufficient on its own, because the form is
 * also written to imperatively: `applyFieldWrite` stages a value with no
 * input involved, and it routes here via `getPendingChanges`. Filtering at
 * this single choke point closes both doors at once.
 *
 * **Safe to omit rather than null out**: `UpdateObjectRecordUseCase` merges
 * (`{**record.data, **accepted_data}`), so a key that isn't sent keeps its
 * existing value. Sending `null` would clear it.
 */
function buildEditData(
  activeFields: ObjectTypeField[],
  data: Record<string, unknown>,
  lockedFieldKeys?: ReadonlySet<string>
): Record<string, unknown> {
  const fieldData: Record<string, unknown> = {};
  for (const field of activeFields) {
    if (lockedFieldKeys?.has(field.fieldKey)) continue;
    const value = data[field.fieldKey];
    fieldData[field.fieldKey] = isEmptyValue(value) ? null : value;
  }

  return fieldData;
}

/**
 * Supplementary fields that should start revealed — those already carrying a
 * non-empty value in edit mode. Empty in create mode (no seed data).
 */
function initialRevealedKeys(
  supplementary: ObjectTypeField[],
  seedData: Record<string, unknown> | undefined
): Set<string> {
  if (!seedData) return new Set();
  return new Set(
    supplementary
      .filter((f) => !isEmptyValue(seedData[f.fieldKey]))
      .map((f) => f.fieldKey)
  );
}

/**
 * Partition active fields into the lead set that is always visible (required,
 * part of the type's display label, or a primary-info text field) and the
 * supplementary remainder. Falls back to the first active field when the type
 * has none of those, so the record is always nameable.
 */
function partitionFields(
  activeFields: ObjectTypeField[],
  displayFields: string[] | null
): {alwaysVisible: ObjectTypeField[]; supplementary: ObjectTypeField[]} {
  const displaySet = new Set(displayFields ?? []);
  const alwaysVisible = activeFields.filter(
    (f) => f.required || displaySet.has(f.fieldKey) || isPrimaryInfoField(f)
  );
  const visible =
    alwaysVisible.length > 0 ? alwaysVisible : activeFields.slice(0, 1);
  const visibleIds = new Set(visible.map((f) => f.id));
  const supplementary = activeFields.filter((f) => !visibleIds.has(f.id));
  return {alwaysVisible: visible, supplementary};
}

/**
 * The shared MDM ObjectRecord form — dynamic field grid + supplementary
 * click-to-add rows, used for both create and edit. Hosted by
 * `ObjectRecordCreateModal` / `ObjectRecordEditModal` (admin) and the Library
 * surfaces (sidebar / browse / bucket pencil). Owns react-hook-form state and
 * validation; the host supplies the surrounding dialog chrome and submit button.
 */
export const ObjectRecordForm = forwardRef<
  ObjectRecordFormHandle,
  ObjectRecordFormProps
>(
  (
    {
      objectType,
      formId,
      onValidSubmit,
      serverErrors,
      mode = 'create',
      initialData,
      collapseOptionalFields = false,
      allowAddField = false,
      disabled = false,
      erpManagedFieldKeys,
      erpWritableFieldKeys,
      objectRecordId,
      onValuesChange,
      onDirtyChange,
    },
    ref
  ) => {
    const isEdit = mode === 'edit';
    const {userRole} = useAppContext();
    // UX-only gate (uxConventions §9.2: hide, don't disable) — the backend
    // fully enforces Quality+ on the write endpoints regardless. A
    // sub-Quality user still sees the field is ERP-synced via
    // `SyncedFieldMarker`, they just never see the writable affordance.
    const canWriteErp = canWriteErpFields(userRole);
    const erpManaged = useMemo(
      () => new Set(erpManagedFieldKeys ?? []),
      [erpManagedFieldKeys]
    );
    // A Quote record's projected fields are the quote engine's: the next sync rewrites
    // them, so they get the same lock as an ERP-synced field, with the workspace as the
    // place to change them. Owned fields (progress, ERP quote number) stay editable.
    const quoteDeskLocked = useMemo(
      () =>
        new Set(
          objectType.moduleTag === QUOTE_MODULE_TAG
            ? [...QUOTE_DESK_FIELD_KEYS, ...QUOTE_PROGRESS_FIELD_KEYS]
            : []
        ),
      [objectType.moduleTag]
    );
    // Every key a save must leave alone, whichever system writes it.
    const lockedKeys = useMemo(
      () => new Set([...erpManaged, ...quoteDeskLocked]),
      [erpManaged, quoteDeskLocked]
    );
    const erpWritable = useMemo(
      () =>
        canWriteErp ? new Set(erpWritableFieldKeys ?? []) : new Set<string>(),
      [canWriteErp, erpWritableFieldKeys]
    );
    // Which field's write-through dialog is open, if any. Local state: it's
    // transient UI, and it must not round-trip through the form.
    const [writeBackFieldKey, setWriteBackFieldKey] = useState<
      string | undefined
    >();
    const writeBackField = useMemo(
      () =>
        writeBackFieldKey === undefined
          ? undefined
          : objectType.fields.find((f) => f.fieldKey === writeBackFieldKey),
      [writeBackFieldKey, objectType.fields]
    );

    const activeFields = useMemo(
      () =>
        objectType.fields
          .filter((f) => f.isActive)
          .sort((a, b) => a.sortOrder - b.sortOrder),
      [objectType.fields]
    );

    const {alwaysVisible, supplementary} = useMemo(
      () => partitionFields(activeFields, objectType.displayFields),
      [activeFields, objectType.displayFields]
    );

    // Supplementary fields the user has chosen to fill in. Once revealed, a field
    // renders as a full input rather than a compact "+ Add value" row. In edit
    // mode, supplementary fields that already carry a value start revealed.
    const [revealedKeys, setRevealedKeys] = useState<Set<string>>(() =>
      initialRevealedKeys(supplementary, isEdit ? initialData : undefined)
    );

    // Admin behavior (collapse off) renders every field inline; collapse mode
    // leads with the always-visible set and offers the rest as click-to-add rows.
    const showSupplementary =
      collapseOptionalFields && supplementary.length > 0;
    const inlineFields = showSupplementary ? alwaysVisible : activeFields;

    const schema = useMemo(
      () => buildDynamicSchema(activeFields),
      [activeFields]
    );

    const {
      setValue,
      watch,
      handleSubmit,
      formState: {errors, isDirty},
    } = useForm<Record<string, unknown>>({
      resolver: zodResolver(schema),
      defaultValues: initialData ?? {},
    });

    const fieldValues = watch();
    const useCustomSuffix = Boolean(fieldValues._useCustomSuffix);
    const customSuffix = (fieldValues._customSuffix as string) ?? '';

    // Coerces staged Otto writes into persist-shape before Save — a frozen
    // Home Recommendation's `field_write` may carry an uncoerced LLM value
    // (JSON-string list, quoted bool/number, an object_reference code); see
    // `field-write-coercion.ts`. Sequence numbers guard against an older
    // write's async coercion (object_reference lookups hit the network)
    // clobbering a newer write to the same field.
    const apiToken = useApiToken();
    const resolveObjectReferenceCode = useMemo(
      () => createObjectReferenceCodeResolver(apiToken),
      [apiToken]
    );
    const coercionSeqRef = useRef<Record<string, number>>({});

    // Shared validated-submit body used by both the visible Save button (the
    // form's `onSubmit`) and the imperative `submitPending` (Otto auto-save).
    // Awaits the parent's mutation so `submitPending` can sequence on its
    // completion.
    const submitValid = async (data: Record<string, unknown>) => {
      if (isEdit) {
        await onValidSubmit({
          data: buildEditData(activeFields, data, lockedKeys),
        });
        return;
      }

      // eslint-disable-next-line @typescript-eslint/naming-convention
      const {_useCustomSuffix: _u, _customSuffix: _c, ...fieldData} = data;
      await onValidSubmit({
        data: fieldData,
        codeSuffix: useCustomSuffix && customSuffix ? customSuffix : undefined,
      });
    };

    useImperativeHandle(
      ref,
      () => ({
        applyFieldWrite(fieldKey, value) {
          // An ERP-managed field can only be changed through write-through,
          // which writes the ERP first and lands the value locally only once
          // it reads back confirmed. Staging one here would paint a value the
          // MDM PATCH now (correctly) refuses to carry, leaving the form
          // showing a change that was never saved anywhere. Refuse loudly in
          // the console rather than silently: an agent reaching this branch
          // is a prompt/tooling bug worth seeing.
          if (erpManaged.has(fieldKey)) {
            console.warn(
              `[ObjectRecordForm] refusing to stage ERP-managed field "${fieldKey}" — ` +
                'it must go through the ERP write-through path.'
            );
            return;
          }

          // Same for a quote's projected field: the quote workspace is its only writer.
          if (quoteDeskLocked.has(fieldKey)) {
            console.warn(
              `[ObjectRecordForm] refusing to stage Quote Desk field "${fieldKey}" — ` +
                'it changes in the quote workspace.'
            );
            return;
          }

          setValue(fieldKey, value, {shouldValidate: true, shouldDirty: true});

          const field = activeFields.find((f) => f.fieldKey === fieldKey);
          const schema = field
            ? toCoercionSchema(field)
            : ({kind: 'other'} as const);
          if (schema.kind === 'other') return;

          const seq = (coercionSeqRef.current[fieldKey] ?? 0) + 1;
          coercionSeqRef.current[fieldKey] = seq;
          const applyCoercion = async () => {
            const result = await coerceFieldWriteForStaging(
              schema,
              value,
              resolveObjectReferenceCode
            );
            if (coercionSeqRef.current[fieldKey] !== seq) return;
            if (result.value === value) return;
            setValue(fieldKey, result.value, {
              shouldValidate: true,
              shouldDirty: true,
            });
          };

          void applyCoercion();
        },
        getPendingChanges() {
          return buildEditData(activeFields, watch(), lockedKeys);
        },
        async submitPending() {
          return new Promise<boolean>((resolve) => {
            // Idempotency: skip the PATCH when the staged values match the
            // last-persisted seed, so repeated turn-settle / swap saves don't
            // churn identical writes.
            const current = buildEditData(activeFields, watch(), lockedKeys);
            const seed = buildEditData(
              activeFields,
              initialData ?? {},
              lockedKeys
            );
            if (JSON.stringify(current) === JSON.stringify(seed)) {
              resolve(true);
              return;
            }

            void handleSubmit(
              async (data) => {
                try {
                  await submitValid(data);
                  resolve(true);
                } catch {
                  // PATCH rejected (e.g. 422) — keep staged values; the next
                  // settle retries once Otto fills the rest.
                  resolve(false);
                }
              },
              () => {
                // RHF validation blocked — keep staged values staged.
                resolve(false);
              }
            )();
          });
        },
      }),
      [
        activeFields,
        resolveObjectReferenceCode,
        setValue,
        watch,
        handleSubmit,
        initialData,
        submitValid,
      ]
    );

    useEffect(() => {
      onDirtyChange?.(isDirty);
    }, [isDirty, onDirtyChange]);

    // Opt-in: stream live field values to a host (Otto's RHS diff strip). Emit
    // an initial snapshot, then push every subsequent change via the watch
    // subscription. Gated on `onValuesChange` so admin modals stay unaffected.
    useEffect(() => {
      if (!onValuesChange) return;
      onValuesChange(watch());
      const sub = watch((values) => {
        onValuesChange(values as Record<string, unknown>);
      });
      return () => {
        sub.unsubscribe();
      };
    }, [watch, onValuesChange]);

    // Merge client-side and server-side errors
    const mergedErrors: Record<string, string> = {...serverErrors};
    for (const [key, err] of Object.entries(errors)) {
      if (err?.message) mergedErrors[key] = err.message;
    }

    const revealField = (fieldKey: string) => {
      setRevealedKeys((prev) => {
        const next = new Set(prev);
        next.add(fieldKey);
        return next;
      });
    };

    // Collapse a revealed supplementary field back to its compact row and clear
    // any value the user entered, so "remove" actually removes it from the record.
    const collapseField = (fieldKey: string) => {
      setRevealedKeys((prev) => {
        const next = new Set(prev);
        next.delete(fieldKey);
        return next;
      });
      setValue(fieldKey, undefined, {shouldValidate: true, shouldDirty: true});
    };

    // Long-text fields (Tribal Knowledge, description, notes, ...) get the
    // roomier MarkdownTextField editor and read better spanning the full row
    // rather than being squeezed into one half of the 2-column grid.
    const isLongText = (field: ObjectTypeField) =>
      field.fieldType === 'text' && LONG_TEXT_FIELD_KEYS.has(field.fieldKey);

    const renderField = (field: ObjectTypeField) => {
      const isErpManaged = erpManaged.has(field.fieldKey);
      // Writability is deliberately not derived from the delegated
      // (synced) set — see get_erp_record_provenance.py's module
      // docstring: deriving one from the other would make a successful
      // write un-write the field, since the very act of writing flips its
      // stamp from `sync` to `user` and drops it out of the delegated set.
      // A field can be writable but not currently delegated (just
      // written), so both the lock and the marker must key off either
      // condition, not `isErpManaged` alone.
      const isErpWritable =
        objectRecordId !== undefined && erpWritable.has(field.fieldKey);
      const isQuoteDesk = quoteDeskLocked.has(field.fieldKey);
      const renderer = (
        <MasterDataFieldRenderer
          key={field.id}
          field={field}
          value={fieldValues[field.fieldKey]}
          onChange={(value) => {
            setValue(field.fieldKey, value, {
              shouldValidate: true,
              shouldDirty: true,
            });
          }}
          error={mergedErrors[field.fieldKey]}
          disabled={disabled || isErpManaged || isErpWritable || isQuoteDesk}
        />
      );

      if (isQuoteDesk)
        return (
          <div key={field.id}>
            {renderer}
            <div className="mt-1">
              {QUOTE_PROGRESS_FIELD_KEYS.includes(field.fieldKey) ? (
                <SyncedFieldMarker
                  sourceLabel="quote workspace"
                  reason="Set by Mark done, Decline or Reopen in the quote workspace, which records who changed it and when."
                />
              ) : (
                <SyncedFieldMarker
                  sourceLabel="Quote Desk"
                  reason="This field comes from the quote engine and is rewritten on every sync. Change it in the quote workspace."
                />
              )}
            </div>
          </div>
        );

      if (!isErpManaged && !isErpWritable) return renderer;

      // Disabled *with* a stated reason, per uxConventions §9.2: this is a
      // state-based block, not a permission one — the same user can edit
      // this field by unmapping it — so a bare greyed-out input would be
      // the drift signal, not the fix.
      //
      // A writable field is the same lock with somewhere to unlock to: the
      // marker becomes an action that opens the write-through dialog.
      return (
        <div key={field.id}>
          {renderer}
          <div className="mt-1">
            {isErpWritable ? (
              <WriteBackFieldMarker
                sourceLabel="Cetec"
                onClick={() => {
                  setWriteBackFieldKey(field.fieldKey);
                }}
              />
            ) : (
              <SyncedFieldMarker sourceLabel="Cetec" />
            )}
          </div>
        </div>
      );
    };

    // Only meaningful inside the `grid grid-cols-2` lead-fields row below —
    // the supplementary click-to-reveal list isn't a grid, so long-text
    // fields there are already full-width without this class.
    const renderGridField = (field: ObjectTypeField) => (
      <div
        key={field.id}
        className={isLongText(field) ? 'col-span-2' : undefined}
      >
        {renderField(field)}
      </div>
    );

    return (
      <form
        id={formId}
        onSubmit={handleSubmit(submitValid)}
        className="space-y-5"
      >
        {/* Lead fields — display + info + required, identified up front. */}
        {activeFields.length > 0 ? (
          <div className="grid grid-cols-2 gap-x-6 gap-y-5">
            {inlineFields.map((field) => renderGridField(field))}
          </div>
        ) : (
          <p className="text-sm text-gray-500">
            No fields configured for this type.
          </p>
        )}

        {/* Supplementary fields — shown as compact click-to-add rows so they're
          discoverable without crowding the form. Revealing one expands it into
          a full input in place. */}
        {showSupplementary && (
          <div className="space-y-1.5">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Additional fields
            </p>
            {supplementary.map((field) =>
              revealedKeys.has(field.fieldKey) ? (
                <div key={field.id} className="relative pt-1">
                  {renderField(field)}
                  <button
                    type="button"
                    onClick={() => {
                      collapseField(field.fieldKey);
                    }}
                    disabled={disabled}
                    className="absolute right-0 top-1 inline-flex items-center gap-1 text-xs font-medium text-gray-400 transition-colors hover:text-gray-600"
                  >
                    <X className="h-3 w-3" />
                    Remove
                  </button>
                </div>
              ) : (
                <button
                  key={field.id}
                  type="button"
                  onClick={() => {
                    revealField(field.fieldKey);
                  }}
                  disabled={disabled}
                  className="flex w-full items-center justify-between rounded-md border border-gray-100 px-3 py-2 text-left text-sm text-gray-700 transition-colors hover:bg-gray-50"
                >
                  <span>{field.label}</span>
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-primary-600">
                    <Plus className="h-3.5 w-3.5" />
                    Add value
                  </span>
                </button>
              )
            )}
          </div>
        )}

        {/* Inline add-field — adds a new attribute to the type on the fly; the new
          field is revealed immediately so its value can be captured here. */}
        {allowAddField && !disabled && (
          <div className="border-t border-gray-100 pt-4">
            <AddFieldForm
              typeId={objectType.id}
              activeFields={activeFields}
              onFieldAdded={(field) => {
                revealField(field.fieldKey);
              }}
            />
          </div>
        )}

        {/* System code — demoted to a disclosure. The code is an internal
          identifier; users identify records by display label. Create-only —
          codes are immutable once assigned. */}
        {!isEdit && (
          <SystemCodeDisclosure
            objectType={objectType}
            useCustomSuffix={useCustomSuffix}
            customSuffix={customSuffix}
            setValue={setValue}
          />
        )}

        {/* Write-through lives outside the form's own submit path on
          purpose: it writes to Cetec first and only then updates MATIC's
          copy, so it must not be bundled into an ordinary Save. */}
        {writeBackField && objectRecordId ? (
          <CetecFieldWriteDialog
            open
            objectRecordId={objectRecordId}
            typeId={objectType.id}
            fieldKey={writeBackField.fieldKey}
            fieldLabel={writeBackField.label}
            fieldType={writeBackField.fieldType}
            initialValue={fieldValues[writeBackField.fieldKey]}
            sourceLabel="Cetec"
            onWritten={(fieldKey, value) => {
              // ShouldDirty: false — the value is already persisted in both
              // Cetec and MATIC, so marking the form dirty would arm an
              // ordinary Save for a change that has already landed.
              setValue(fieldKey, value, {
                shouldValidate: true,
                shouldDirty: false,
              });
            }}
            onClose={() => {
              setWriteBackFieldKey(undefined);
            }}
          />
        ) : undefined}
      </form>
    );
  }
);

ObjectRecordForm.displayName = 'ObjectRecordForm';

/**
 * The create-only system-code escape hatch — a disclosure showing the
 * auto-generated code with an optional custom-suffix override for orgs
 * mirroring an external numbering scheme. Fetches the next suffix itself so the
 * query runs only when rendered (create mode).
 */
function SystemCodeDisclosure({
  objectType,
  useCustomSuffix,
  customSuffix,
  setValue,
}: {
  objectType: ObjectType;
  useCustomSuffix: boolean;
  customSuffix: string;
  setValue: UseFormSetValue<Record<string, unknown>>;
}) {
  const {data: suffixData} = useNextSuffix(objectType.id);

  const codePreview = suffixData
    ? `${suffixData.prefix}-${useCustomSuffix && customSuffix ? customSuffix : suffixData.suggestedSuffix}`
    : `${objectType.prefix}-...`;

  return (
    <details className="rounded-md border border-gray-100 bg-gray-50/50 px-3 py-2 text-xs text-gray-500">
      <summary className="cursor-pointer select-none">
        System code:{' '}
        <span className="font-mono text-gray-600">{codePreview}</span>
      </summary>
      <div className="mt-3 space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500">
            Override the auto-generated suffix
          </span>
          <Toggle
            checked={useCustomSuffix}
            onChange={(checked) => {
              setValue('_useCustomSuffix', checked, {shouldDirty: true});
              if (!checked) setValue('_customSuffix', '', {shouldDirty: true});
            }}
            label="Enable custom suffix"
          />
        </div>
        {useCustomSuffix && (
          <Input
            value={customSuffix}
            onChange={(e) => {
              setValue('_customSuffix', e.target.value, {shouldDirty: true});
            }}
            placeholder={suffixData?.suggestedSuffix ?? '0001'}
            className="text-sm"
          />
        )}
      </div>
    </details>
  );
}
